import { IChatActionList, IChatActionsData } from "@core/base/chat-action-base";
import { ChatBase } from "@core/base/chat-base";
import { SubscriptionBase } from "@core/base/subscription-base";
import { ICoreDependencies } from "@core/core-boot/core-boot.types";
import { OfficeChatRepository } from "@core/office/office-chat-repository/office-chat-repository";
import { OfficeChatSetting } from "@core/office/office-chat-setting/office-chat-setting";
import { OfficeChatBase } from "@core/office/office-chat/office-chat-base";
import { OfficeChatAdapterServiceV1 } from "@core/office/office-chat/office-chat-v1";
import type { ISkillQueueProgress } from "@core/office/office-chat/office-chat-v1/office-chat-v1";
import { OfficeService } from "@core/office/office-service/office-service";
import { OfficeSessionService } from "@core/office/office-session/office.session.service";
import { OfficeGroupChat } from '@core/office/office-group-chat/office-group-chat';
import { ChatUtilsService } from "@core/shared/chat-utils.service";
import type { IChatMessage } from '@core/types/chat-message.types';
import type { IChatMessageResult } from '@core/types/chat.types';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable } from "rxjs";

export class OfficeChat extends ChatBase implements ChatBase
{
    private isLoading = new BehaviorSubject<boolean>(false);
    private officeChat: OfficeChatBase = new OfficeChatAdapterServiceV1(
        this.dependencies,
        this.office,
        this.chatRepository,
        this.chatUtils,
        this.settingService,
        this.sessionService,
        this.subscriptionService,
        this.groupChat,
    );

    constructor(
        private dependencies: ICoreDependencies,
        private office: OfficeService,
        private chatRepository: OfficeChatRepository,
        private chatUtils: ChatUtilsService,
        private settingService: OfficeChatSetting,
        private sessionService: OfficeSessionService,
        private subscriptionService: SubscriptionBase,
        private groupChat?: OfficeGroupChat,
    )
    {
        super();
    }

    public $isLoading(): Observable<boolean>
    {
        // A live send (isLoading) or a send resumed from a previous session whose
        // answer we are still polling for (awaitingAnswer) both keep the composer
        // in its loading state.
        return combineLatest([
            this.isLoading,
            this.officeChat.$isAwaitingAnswer(),
        ]).pipe(
            map(([ isLoading, awaitingAnswer ]) => isLoading || awaitingAnswer),
            distinctUntilChanged(),
        );
    }

    public $isMessagesLoading(): Observable<boolean>
    {
        return this.officeChat.$isMessagesLoading();
    }

    public $skillQueueProgress(): Observable<ISkillQueueProgress | null>
    {
        return this.officeChat.$skillQueueProgress();
    }

    public clearMessages(): Promise<void>
    {
        return this.officeChat.clearMessages();
    }

    public deleteMessage(index: number): Promise<void>
    {
        return this.officeChat.deleteMessage(index);
    }

    public deleteLastUserMessage(): void
    {
        this.officeChat.deleteLastUserMessage();
    }

    public getMessages(): Observable<IChatMessage[]>
    {
        return this.officeChat.getMessages();
    }

    public getMessagesValue(): IChatMessage[]
    {
        return this.officeChat.getMessagesValue();
    }

    public setHistoryForCurrentThread(enabled: boolean): Promise<boolean>
    {
        return this.officeChat.setHistoryForCurrentThread(enabled);
    }
    public currentThreadId$(): Observable<string | null> { return this.officeChat.currentThreadId$(); }

    public override onDestroy(): void
    {
        this.officeChat.onDestroy();
    }

    public async sendMessage(data: string, dataAction: IChatActionsData | null, actions: IChatActionList, retry = false): Promise<IChatMessageResult>
    {
        this.isLoading.next(true);
        try
        {
            return await this.officeChat.sendMessage(data, dataAction, actions, retry);
        }
        finally
        {
            this.isLoading.next(false);
        }
    }

    public async sendMessageWithoutSaving(data: string, dataAction: IChatActionsData | null, actions: IChatActionList): Promise<IChatMessageResult>
    {
        this.isLoading.next(true);
        try
        {
            return await this.officeChat.sendMessageWithoutSaving(data, dataAction, actions);
        }
        finally
        {
            this.isLoading.next(false);
        }
    }

    public writeBotMessage(text: string): Promise<void>
    {
        return this.officeChat.writeBotMessage(text);
    }

    public getThreadMessages(scope: string): Observable<IChatMessage[]>
    {
        return this.officeChat.getThreadMessages(scope);
    }

    public $isThreadSending(scope: string): Observable<boolean>
    {
        return this.officeChat.$isThreadSending(scope);
    }

    public initThread(scope: string): Promise<void>
    {
        return this.officeChat.initThread(scope);
    }

    public sendMessageToThread(scope: string, text: string): Promise<IChatMessageResult>
    {
        return this.officeChat.sendMessageToThread(scope, text);
    }
}
